import React, { useEffect, useState, useMemo, useRef } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
} from '@material-ui/core';
import axios from 'axios';
import { useApi, identityApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import yaml from 'js-yaml';
import { resolveFormFieldTemplate } from '../utils/resolveFormFieldTemplate';

export const SelectAdoApp = (props: FieldExtensionComponentProps<string>) => {
  const { onChange, formData, required, rawErrors, schema, uiSchema, formContext } = props;

  const adoProjectTemplate = uiSchema['ui:options']?.adoProject || '';
  const resolvedAdoProject = useMemo(() => {
    return resolveFormFieldTemplate(
      String(adoProjectTemplate),
      (formContext.formData || {}) as Record<string, unknown>,
    );
  }, [adoProjectTemplate, formContext.formData]);

  const adoProductTemplate = uiSchema['ui:options']?.adoProduct || '';
  const resolvedAdoProduct = useMemo(() => {
    return resolveFormFieldTemplate(
      String(adoProductTemplate),
      (formContext.formData || {}) as Record<string, unknown>,
    );
  }, [adoProductTemplate, formContext.formData]);

  const endpoint = `/backstage/project/${resolvedAdoProject}/${resolvedAdoProduct}`;

  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const prevProjectRef = useRef(resolvedAdoProject);
  const prevProductRef = useRef(resolvedAdoProduct);

  useEffect(() => {
    if (
      prevProjectRef.current !== resolvedAdoProject ||
      prevProductRef.current !== resolvedAdoProduct
    ) {
      onChange('');
      prevProjectRef.current = resolvedAdoProject;
      prevProductRef.current = resolvedAdoProduct;
    }

    setOptions([]);
    setFetchError(null);

    if (!resolvedAdoProject || !resolvedAdoProduct) {
      setLoading(false);
      setFetchError('Please select a valid ADO Project and Product');
      return;
    }

    setLoading(true);
    async function fetchApps() {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
        const credentials = await identityApi.getCredentials();
        const token = credentials.token;
        const response = await axios.post(
          `${baseUrl}/aws/params`,
          { name: endpoint },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );

        const rawValue = response.data.value || '';
        if (!rawValue) {
          throw new Error('Invalid response: No value returned');
        }

        const parsed = yaml.load(rawValue);
        let appList: string[] = [];

        if (parsed && typeof parsed === 'object' && Array.isArray((parsed as any).apps)) {
          appList = (parsed as any).apps
            .map((app: unknown) => {
              if (typeof app === 'string') return app;
              if (app && typeof app === 'object' && (app as { name?: string }).name) {
                return (app as { name: string }).name;
              }
              return null;
            })
            .filter((a: string | null): a is string => a !== null && a !== 'infra');
        } else if (typeof parsed === 'string') {
          appList = parsed
            .split(',')
            .map((a: string) => a.trim())
            .filter((a: string) => a && a !== 'infra');
        } else {
          throw new Error('Apps list not found in response');
        }

        if (appList.length === 0) {
          setFetchError('No applications found for this product');
        }

        setOptions(appList);
        setFetchError(null);
      } catch (err) {
        console.error('Error fetching ADO apps:', err);
        setFetchError('Failed to fetch applications. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    fetchApps();
  }, [resolvedAdoProject, resolvedAdoProduct, endpoint, identityApi, discoveryApi, onChange]);

  useEffect(() => {
    if (required && (!formData || formData === '')) {
      setValidationError('Application is required');
    } else {
      setValidationError(null);
    }
  }, [formData, required]);

  const combinedError = validationError || fetchError;

  return (
    <FormControl fullWidth required={required} error={!!rawErrors?.length || !!combinedError}>
      <InputLabel>{schema.title}</InputLabel>
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Select
          value={formData || ''}
          onChange={e => onChange(e.target.value as string)}
          disabled={!resolvedAdoProject || !resolvedAdoProduct}
        >
          {options.map(option => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      )}
      {combinedError && <FormHelperText>{combinedError}</FormHelperText>}
    </FormControl>
  );
};
